import React, { useState } from "react";
import { Link } from "react-router-dom";
import heroImage from "../assets/portfolio.jpg";
import primerevixImg from "../assets/primerevix_portfolio.jpg";
import oilTradersImg from "../assets/oil_traders_portfolio.jpg";
import tlcleatherImg from "../assets/tlcleather_portfolio.png";
import museandsilkImg from "../assets/museandsilk_portfolio.png";
import randomrubixImg from "../assets/randomrubix_portfolio.png";

const projects = [
  {
    id: 1,
    title: "PrimeRevix Medical Billing Platform",
    category: "Healthcare & SaaS",
    image: primerevixImg,
    techStack: ["React", "Node.js", "PostgreSQL", "Tailwind CSS"],
    shortDesc: "A custom medical billing and revenue cycle management system designed for healthcare clinics.",
    longDesc: "PrimeRevix is a dedicated workflow management platform designed to streamline medical billing and claim submissions for US-based healthcare practices. The application handles patient intake, validates billing codes, and tracks insurance claim reimbursement lifecycles from submission to payout.",
    problem: "Clinics faced high claim denial rates and slow turnaround times due to manual entry errors and complex insurance rules.",
    solution: "We built a centralized system that automatically validates patient eligibility, cross-checks medical codes against insurance rules, and flags potential errors before claims are submitted to clearinghouses.",
    features: [
      "Automated patient eligibility checks",
      "Real-time billing code validation and error flagging",
      "Comprehensive claim status tracking and history logs",
      "Interactive financial reports and practice performance dashboards",
      "HIPAA-compliant data encryption and user access controls"
    ],
    glowClass: "hover:shadow-[0_20px_50px_rgba(59,130,246,0.18)] hover:border-blue-300",
    themeColor: "text-blue-600",
    bgColor: "bg-blue-50"
  },
  {
    id: 2,
    title: "Chaudhary Oil Traders ERP & POS",
    category: "Custom Business Software",
    image: oilTradersImg,
    techStack: ["React", "Node.js", "Express", "MongoDB", "Tailwind CSS"],
    shortDesc: "A custom inventory tracking, sales point-of-sale, and ledger management system for a fuel and oil trading business.",
    longDesc: "A bespoke ERP and POS system built for Chaudhary Oil Traders to replace manual spreadsheets. The platform tracks fuel inventory, customer credit balances, supplier ledgers, and dispatch sales across multiple retail locations in real time.",
    problem: "Manual records of fuel tank volumes, credit sales, and customer ledgers led to stock discrepancies and administrative delays.",
    solution: "We developed an integrated portal featuring automatic tank volume calculators, credit limit alerts, digital invoice generation, and customer ledgers.",
    features: [
      "Real-time fuel inventory tracking and volume calculations",
      "Integrated customer and supplier ledger books",
      "Credit balance monitoring and billing alerts",
      "POS billing and automated invoice generation",
      "Monthly sales reports and profit audit dashboards"
    ],
    glowClass: "hover:shadow-[0_20px_50px_rgba(168,85,247,0.18)] hover:border-purple-300",
    themeColor: "text-purple-600",
    bgColor: "bg-purple-50"
  },
  {
    id: 3,
    title: "TLC Leather Online Store",
    category: "E-Commerce",
    image: tlcleatherImg,
    techStack: ["Next.js", "Shopify Storefront API", "Tailwind CSS", "GraphQL"],
    shortDesc: "A fast, custom-built e-commerce storefront for a handcrafted leather goods brand.",
    longDesc: "We developed a headless e-commerce store for TLC Leather to showcase and sell their premium handcrafted leather products. Built with Next.js on top of Shopify's backend, the site delivers fast page loads, custom product layouts, and an interactive monogramming tool for personalizing items.",
    problem: "The brand needed a modern storefront that loaded fast on mobile and allowed customers to preview custom monogram styling on leather products before purchasing.",
    solution: "We built a headless storefront using Next.js for high performance and connected it to Shopify via GraphQL. We also added a custom front-end monogram builder for real-time text engraving previews.",
    features: [
      "Custom real-time monogramming and engraving previews",
      "Headless e-commerce setup using Shopify Storefront API",
      "Fast, mobile-optimized catalog and product filters",
      "Secure single-page checkout flow",
      "Custom order manager for personalized items"
    ],
    glowClass: "hover:shadow-[0_20px_50px_rgba(217,119,6,0.18)] hover:border-amber-300",
    themeColor: "text-amber-600",
    bgColor: "bg-amber-50"
  },
  {
    id: 4,
    title: "Muse & Silk Accessories Shop",
    category: "E-Commerce",
    image: museandsilkImg,
    techStack: ["React", "Vite", "Tailwind CSS", "Shopify API", "Framer Motion"],
    shortDesc: "A modern, responsive e-commerce storefront for a fashion accessories and eyewear brand.",
    longDesc: "A custom-designed online store for Muse & Silk, a fashion brand specializing in silk scarves and designer eyewear. The website features interactive lookbooks, product collection filtering, and a smooth checkout journey with optimized page performance.",
    problem: "The client wanted a clean, visually appealing storefront that highlighted product details and offered a shopping experience across mobile and desktop devices.",
    solution: "We built a clean React frontend with subtle transition animations and integrated Shopify APIs for seamless product inventory, shopping cart, and customer checkout flows.",
    features: [
      "Modern, high-performance product catalog layouts",
      "Interactive 'Shop-the-Look' styled lookbooks",
      "Clean filtering options by category, color, and size",
      "Fast-loading image galleries optimized for high resolution",
      "Seamless slide-out cart drawer and payment gateway integration"
    ],
    glowClass: "hover:shadow-[0_20px_50px_rgba(236,72,153,0.18)] hover:border-pink-300",
    themeColor: "text-pink-600",
    bgColor: "bg-pink-50"
  },
  {
    id: 5,
    title: "Random Rubix Online Store",
    category: "E-Commerce",
    image: randomrubixImg,
    techStack: ["React", "Node.js", "MongoDB", "Express", "Tailwind CSS"],
    shortDesc: "An e-commerce website for purchasing speedcubes, Rubik's cubes, and puzzle accessories.",
    longDesc: "Random Rubix is an e-commerce website designed and built for cubing enthusiasts. The store allows users to explore and buy a wide range of Rubik's cubes, from standard 3x3s to advanced speedcubes and specialty puzzles, with a smooth shopping experience.",
    problem: "The business needed a dedicated online shop where customers could easily browse different types of Rubik's cubes, filter by brands, and complete purchases on any device.",
    solution: "We built a responsive web store using React and a Node.js/MongoDB backend, featuring easy product navigation, an integrated shopping cart, and support for popular payment methods.",
    features: [
      "Clean, categorized layout for all cube sizes (2x2 to 7x7) and specialty shapes",
      "Product search and filters for brands and puzzle difficulty",
      "Simple shopping cart and secure checkout form",
      "Cash on Delivery (COD) and local payment gateway support",
      "Admin panel for managing inventory and order statuses"
    ],
    glowClass: "hover:shadow-[0_20px_50px_rgba(16,185,129,0.18)] hover:border-emerald-300",
    themeColor: "text-emerald-600",
    bgColor: "bg-emerald-50"
  }
];

const testimonials = [
  {
    name: "Dr. Arthur Vance",
    role: "Managing Director, Apex Medical Group",
    quote: "Atronm's work on PrimeRevix exceeded our expectations. The integration of AI agents has cut our prior authorization bottlenecks by half and simplified billing immensely.",
    rating: 5,
  },
  {
    name: "Chaudhary Nabeel",
    role: "Owner, Chaudhary Oil Traders",
    quote: "The inventory and POS system completely transformed our operations. We no longer worry about ledger errors or credit leaks. Highly professional team and clean software.",
    rating: 5,
  },
  {
    name: "Sarah Jenkins",
    role: "Product Head, FinTech Flow",
    quote: "Working with Atronm was a breeze. Their attention to performance optimization and neat UI designs makes them our go-to team for custom web applications.",
    rating: 5,
  },
  {
    name: "Mohammad Harris",
    role: "Co-Founder, RetailPulse",
    quote: "The speed at which the Atronm team delivered our application was impressive. Clean code, great responsive styling, and solid post-launch support.",
    rating: 5,
  }
];

export default function PortfolioPage() {
  const [selectedProject, setSelectedProject] = useState(null);

  // Duplicate testimonials to create seamless loop for marquee train
  const doubledTestimonials = [...testimonials, ...testimonials];

  return (
    <section className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-800 font-sans relative overflow-hidden">
      {/* Ambient background glow blobs */}
      <div className="absolute top-[20%] left-[-150px] w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] bg-blue-300/5 rounded-full blur-3xl pointer-events-none animate-pulse duration-[14s]"></div>
      <div className="absolute top-[60%] right-[-150px] w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] bg-purple-300/5 rounded-full blur-3xl pointer-events-none animate-pulse duration-[16s]"></div>
      
      {/* Hero Header Banner */}
      <div
        className="relative bg-cover bg-center text-white py-16 sm:py-28 px-4 sm:px-6 text-center shadow-lg"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-[2px]"></div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="inline-block px-3 py-1 bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-semibold uppercase tracking-wider rounded-full mb-3">
            Our Work
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 tracking-tight drop-shadow-md">
            Portfolio
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-slate-100 max-w-2xl mx-auto drop-shadow-sm font-light">
            Explore our real-world custom software applications and AI solutions.
          </p>
        </div>
      </div>

      {/* Projects Section */}
      <div className="max-w-6xl mx-auto py-20 px-6">
        <div className="text-center max-w-3xl mx-auto mb-16" data-aos="fade-down">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Featured Projects
          </h2>
          <p className="text-gray-600 text-base sm:text-lg mt-4">
            Click on any project card to view the technical implementation details, problem statements, and key outcomes.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {projects.map((project) => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              className={`cursor-pointer bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm hover:-translate-y-3 transition-all duration-300 group flex flex-col justify-between ${project.glowClass}`}
              data-aos="fade-up"
              data-aos-delay={project.id * 100}
            >
              <div>
                <div className="relative overflow-hidden aspect-video">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    loading="eager"
                    fetchPriority="high"
                    decoding="sync"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`inline-block px-3 py-1 text-xs font-bold uppercase rounded-full ${project.bgColor} ${project.themeColor} shadow-sm border border-white/50`}>
                      {project.category}
                    </span>
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="text-2xl font-extrabold text-slate-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">
                    {project.shortDesc}
                  </p>
                </div>
              </div>

              <div className="px-8 pb-8">
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.techStack.map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 bg-slate-50 border border-slate-200/80 rounded-lg text-xs font-medium text-slate-600"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <div className={`inline-flex items-center gap-1.5 text-sm font-bold ${project.themeColor} group-hover:translate-x-1.5 transition-transform duration-300`}>
                  View Project Details &rarr;
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials Infinite Marquee Section */}
      <div className="py-20 bg-slate-50 border-y border-slate-100 overflow-hidden relative">
        <div className="max-w-6xl mx-auto px-6 mb-16 text-center" data-aos="fade-down">
          <div className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold uppercase tracking-wider rounded-full mb-3">
            Testimonials
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            What Our Clients Say
          </h2>
          <p className="text-gray-600 text-base sm:text-lg mt-4">
            See how Atronm helps businesses build robust web and custom software systems globally.
          </p>
        </div>

        {/* Infinite Train Track Wrapper */}
        <div className="relative w-full flex items-center">
          {/* Left/Right fading gradients to look premium */}
          <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none"></div>

          <div className="w-full overflow-hidden">
            <div className="animate-marquee gap-8 py-4">
              {doubledTestimonials.map((testimonial, idx) => (
                <div
                  key={idx}
                  className="w-[300px] sm:w-[350px] bg-white border border-slate-100 rounded-3xl p-6 sm:p-8 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow duration-300 flex-shrink-0"
                >
                  <div>
                    {/* Stars */}
                    <div className="flex gap-1 mb-4 text-yellow-400">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg
                          key={i}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.6 3.102-1.196 4.657c-.209.814.67 1.453 1.378.98L10 15.301l4.217 2.855c.708.473 1.587-.166 1.378-.98l-1.196-4.657 3.6-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ))}
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed italic mb-6">
                      "{testimonial.quote}"
                    </p>
                  </div>

                  <div className="border-t border-slate-100 pt-4 flex flex-col">
                    <span className="font-bold text-slate-900 text-base">
                      {testimonial.name}
                    </span>
                    <span className="text-xs text-gray-400 font-medium mt-0.5">
                      {testimonial.role}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="max-w-5xl mx-auto py-20 px-6" data-aos="fade-up">
        <div className="relative rounded-3xl bg-gradient-to-r from-slate-900 to-indigo-950 text-white p-8 sm:p-12 md:p-16 text-center shadow-xl overflow-hidden">
          {/* Decorative blur elements */}
          <div className="absolute -top-24 -left-24 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
              Ready to Turn Your Vision into Reality?
            </h3>
            <p className="text-slate-300 text-sm sm:text-base font-light leading-relaxed">
              Whether you need intelligent AI agents, custom database software, or high-performance web solutions, our expert engineering team is here to design and build it.
            </p>
            <div className="pt-4">
              <Link
                to="/contact"
                className="inline-block bg-white text-slate-900 hover:bg-slate-100 transition-all font-bold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-[0_10px_20px_rgba(255,255,255,0.06)] hover:-translate-y-0.5"
              >
                Start Your Project
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Project Detail Modal Overlay */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm transition-all duration-300">
          <div
            className="relative w-full max-w-4xl bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200"
            data-aos="zoom-in"
          >
            {/* Modal Header Image */}
            <div className="relative aspect-video sm:h-72 w-full overflow-hidden">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="sync"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <span className={`inline-block px-3 py-1 text-xs font-bold uppercase rounded-full ${selectedProject.bgColor} ${selectedProject.themeColor} mb-2 shadow-sm`}>
                  {selectedProject.category}
                </span>
                <h3 className="text-xl sm:text-3xl font-extrabold drop-shadow-md">
                  {selectedProject.title}
                </h3>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 bg-slate-900/60 hover:bg-slate-900 text-white rounded-full p-2.5 transition-colors focus:outline-none"
                aria-label="Close Modal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2.5"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body Contents */}
            <div className="p-6 sm:p-10 overflow-y-auto flex-1 space-y-6">
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Project Overview</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {selectedProject.longDesc}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-50/50 border border-red-100 rounded-2xl p-5">
                  <h5 className="text-sm font-bold text-red-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full"></span>
                    The Problem
                  </h5>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {selectedProject.problem}
                  </p>
                </div>

                <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-5">
                  <h5 className="text-sm font-bold text-emerald-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></span>
                    Our Solution
                  </h5>
                  <p className="text-gray-600 text-xs leading-relaxed">
                    {selectedProject.solution}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Key Features Implemented</h4>
                <ul className="grid sm:grid-cols-2 gap-2 text-xs text-gray-700">
                  {selectedProject.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-4 h-4 text-emerald-500 flex-shrink-0"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Technologies Used</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.techStack.map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 border-t border-slate-100 px-6 py-4 flex justify-end">
              <button
                onClick={() => setSelectedProject(null)}
                className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors focus:outline-none"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
}
